class WebSocketService {
  private static instance: WebSocketService;
  private socket: WebSocket | null = null;
  private reconnectInterval = 5000; // 5 seconds
  private reconnectTimer: NodeJS.Timeout | null = null;
  private messageHandlers: Map<string, Function[]> = new Map();
  private connectionHandlers: Function[] = [];
  private connectionStatusChangeHandlers: ((status: boolean) => void)[] = [];
  private connectionStatus = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private connectionTimeout: NodeJS.Timeout | null = null;
  
  // Important: Add connection limit control
  private static connectionsInProgress = 0;
  private static MAX_CONNECTIONS = 3; // Prevent too many simultaneous connection attempts
  
  private constructor() {}
  
  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }
  
  public connect(): void {
    // Check if already connecting or connected to avoid duplicates
    if (this.socket && (this.socket.readyState === WebSocket.CONNECTING || 
                        this.socket.readyState === WebSocket.OPEN)) {
      console.log('WebSocket already connecting or connected');
      return;
    }
    
    // Implement connection attempt limit
    if (WebSocketService.connectionsInProgress >= WebSocketService.MAX_CONNECTIONS) {
      console.log(`Too many connection attempts in progress (${WebSocketService.connectionsInProgress}), deferring`);
      setTimeout(() => this.connect(), 2000);
      return;
    }
    
    // Clear any pending reconnection timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
    }
    
    // Set connection timeout to prevent hanging
    this.connectionTimeout = setTimeout(() => {
      if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
        console.log('WebSocket connection timeout - forcing disconnect');
        this.socket.close();
        this.socket = null;
        WebSocketService.connectionsInProgress--;
        this.setConnectionStatus(false);
      }
    }, 10000);
    
    WebSocketService.connectionsInProgress++;
    
    // Use explicit backend URL for WebSockets
    const isProduction = process.env.NODE_ENV === 'production';
    const backendUrl = isProduction 
      ? (process.env.REACT_APP_WS_URL || 'wss://the-league-system-backend.johnvjandayan.workers.dev') 
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('Cannot connect to WebSocket: No auth token found');
      WebSocketService.connectionsInProgress--;
      if (this.connectionTimeout) clearTimeout(this.connectionTimeout);
      return;
    }
    
    try {
      this.socket = new WebSocket(`${backendUrl}/ws/notifications/?token=${token}`);
    } catch (e) {
      console.error('Error creating WebSocket:', e);
      WebSocketService.connectionsInProgress--;
      if (this.connectionTimeout) clearTimeout(this.connectionTimeout);
      return;
    }
    
    this.socket.onopen = () => {
      console.log('WebSocket connected');
      WebSocketService.connectionsInProgress--;
      this.reconnectAttempts = 0;
      this.setConnectionStatus(true);
      this.connectionHandlers.forEach(handler => handler());
      
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
    };
    
    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const type = data.type || 'message';
        
        // Call appropriate handlers
        if (this.messageHandlers.has(type)) {
          this.messageHandlers.get(type)?.forEach(handler => handler(data));
        }
        
        // Call generic handlers
        if (this.messageHandlers.has('*')) {
          this.messageHandlers.get('*')?.forEach(handler => handler(data));
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };
    
    this.socket.onclose = (event) => {
      console.log(`WebSocket disconnected: ${event.code} - ${event.reason}`);
      WebSocketService.connectionsInProgress--;
      this.setConnectionStatus(false);
      
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      
      // Implement exponential backoff for reconnection
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        const backoffTime = Math.min(30000, 
          this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts));
        this.reconnectAttempts++;
        
        console.log(`Scheduling reconnect in ${backoffTime}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        
        this.reconnectTimer = setTimeout(() => {
          this.connect();
        }, backoffTime);
      } else {
        console.log(`Maximum reconnection attempts (${this.maxReconnectAttempts}) reached`);
      }
    };
    
    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      WebSocketService.connectionsInProgress--;
      this.setConnectionStatus(false);
    };
  }
  
  private setConnectionStatus(status: boolean): void {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      this.connectionStatusChangeHandlers.forEach(handler => handler(status));
    }
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  public send(message: any): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.error('Cannot send message: WebSocket is not connected');
    }
  }

  public onConnectionStatusChange(handler: (status: boolean) => void): () => void {
    this.connectionStatusChangeHandlers.push(handler);
    
    // Return function to remove the handler
    return () => {
      this.connectionStatusChangeHandlers = this.connectionStatusChangeHandlers.filter(h => h !== handler);
    };
  }

  public onConnect(handler: Function): () => void {
    this.connectionHandlers.push(handler);
    
    // Return function to remove the handler
    return () => {
      this.connectionHandlers = this.connectionHandlers.filter(h => h !== handler);
    };
  }

  public onMessage(type: string, handler: Function): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    
    this.messageHandlers.get(type)?.push(handler);
    
    // Return function to remove the handler
    return () => {
      const handlers = this.messageHandlers.get(type);
      if (handlers) {
        this.messageHandlers.set(type, handlers.filter(h => h !== handler));
      }
    };
  }

  public isConnected(): boolean {
    return !!this.socket && this.socket.readyState === WebSocket.OPEN;
  }
}

export default WebSocketService.getInstance();